import { useCallback, useRef, useState } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { AssignmentPosition } from 'play/support/assignmentPosition';

import { useResizeHandler } from '../../../support/react/hooks';
import { ResizeHandle } from '../resize-handle/ResizeHandle';

export const DEFAULT_SIDEBAR_OVERLAY = false;
export const DEFAULT_SIDEBAR_POSITION = 'right';
export const DEFAULT_SIDEBAR_WIDTH = 450;
export const MIN_SIDEBAR_WIDTH = 300;
export const MAX_SIDEBAR_WIDTH_RATIO = 2 / 3;

export function AssignmentSidebar({
  width,
  setWidth,
  children,
  startResizing,
  stopResizing,
  collapsed,
  sidebarPosition = 'right',
  sidebarOverlay = false,
}: {
  collapsed?: boolean;
  sidebarPosition?: AssignmentPosition;
  sidebarOverlay?: boolean;
  width: number;
  setWidth: React.Dispatch<React.SetStateAction<number>>;
  children: React.ReactNode;
  startResizing?: () => void;
  stopResizing?: () => void;
}) {
  const resizeHandle = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  useResizeHandler(
    resizeHandle,
    useCallback(
      deltaX => {
        setWidth(w =>
          Math.min(
            window.innerWidth * MAX_SIDEBAR_WIDTH_RATIO,
            Math.max(MIN_SIDEBAR_WIDTH, sidebarPosition === 'right' ? w - deltaX : w + deltaX),
          ),
        );
      },
      [sidebarPosition, setWidth],
    ),
    () => {
      setIsResizing(true);
      startResizing?.();
    },
    () => {
      setIsResizing(false);
      stopResizing?.();
    },
  );

  return (
    <Layout
      sidebarPosition={sidebarPosition}
      sidebarOverlay={sidebarOverlay}
      style={{
        width: `${collapsed ? 48 : width}px`,
        transition: `${isResizing ? 'none' : 'width 200ms ease-in-out'}`,
      }}
    >
      {!collapsed && <ResizeHandle ref={resizeHandle} sidebarPosition={sidebarPosition} />}
      {children}
    </Layout>
  );
}

export const Layout = styled.div(
  ({ sidebarPosition, sidebarOverlay }: { sidebarPosition: AssignmentPosition; sidebarOverlay: boolean }) => [
    tw`bg-[#3b4558] relative flex flex-col w-full h-full transition-[left] duration-fast ease-in-out`,
    sidebarPosition === 'right' ? tw`order-2 pl-[2px]` : tw`order-1 pr-[2px]`,
    sidebarOverlay && tw`h-[calc(100% - 50px)] absolute z-overlay top-[50px] right-0`,
    sidebarOverlay && sidebarPosition === 'left' && tw`right-auto left-0`,
  ],
);
